import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stepper, Step, StepLabel, Button, Paper, Grid,
  TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  Chip, Divider, Alert, Card, CardContent, IconButton, Tooltip,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails, List,
  ListItem, ListItemText, ListItemSecondaryAction, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, Switch, FormControlLabel, Collapse,
} from '@mui/material';
import {
  AutoAwesome, PictureAsPdf, Description, Add, Delete, ExpandMore,
  CheckCircle, Article, ArrowBack, ArrowForward, Refresh, Save,
  Business, Person, CalendarToday, Lock, BarChart, AttachMoney,
  Group, TrendingUp, Visibility, ExpandLess, CheckBox, CheckBoxOutlineBlank,
} from '@mui/icons-material';
import { projectService, reportService } from '../services/api';

const STEPS = [
  { label: 'Projet & Sections' },
  { label: 'Contenu' },
  { label: 'Annexes' },
  { label: 'Aperçu & Export' },
];

const ALL_SECTION_KEYS = [
  'object', 'executiveSummary', 'introduction', 'objectives', 'specificity',
  'methodology', 'analysis', 'memberContributions', 'budget',
  'conclusion', 'recommendations', 'lessonsLearned', 'perspectives',
];

const SECTION_LABELS = {
  object: 'Objet du rapport',
  executiveSummary: 'Résumé analytique (Executive Summary)',
  introduction: 'Introduction & Contexte',
  objectives: 'Objectifs du projet',
  specificity: 'Spécificités du projet',
  methodology: 'Méthodologie',
  analysis: 'Résultats & Analyse',
  memberContributions: 'Implication des membres',
  budget: 'Budget & Finances',
  conclusion: 'Conclusion & Synthèse',
  recommendations: 'Recommandations',
  lessonsLearned: 'Leçons apprises',
  perspectives: 'Perspectives & Suite',
};

const SECTION_HINTS = {
  object: 'Décrit l\'objet du rapport : pourquoi il a été produit, à qui il est destiné.',
  executiveSummary: 'Résumé des objectifs, résultats majeurs et recommandations clés (1 page max).',
  introduction: 'Contexte général, problématique et périmètre du projet.',
  objectives: 'Objectifs généraux et spécifiques, résultats attendus, indicateurs de succès.',
  specificity: 'Caractéristiques distinctives, contraintes particulières, défis du projet.',
  methodology: 'Démarche adoptée, outils utilisés et étapes clés du processus.',
  analysis: 'Résultats factuels, succès, difficultés, comparaison aux objectifs initiaux.',
  memberContributions: 'Rôle et implications de chaque membre, leurs tâches et niveau d\'engagement.',
  budget: 'Analyse narrative du budget : consommation, écarts, observations financières.',
  conclusion: 'Synthèse de l\'essentiel du projet et conclusions principales.',
  recommendations: 'Pistes d\'action concrètes et mesurables pour la suite ou correctifs.',
  lessonsLearned: 'Ce qui a bien fonctionné et ce qui pourrait être amélioré.',
  perspectives: 'Prochaines étapes envisagées, opportunités à saisir, risques à surveiller.',
};

const SECTION_GROUPS = [
  {
    label: 'Introduction',
    icon: <Article fontSize="small" />,
    color: '#1a56db',
    keys: ['object', 'executiveSummary', 'introduction', 'objectives', 'specificity'],
  },
  {
    label: 'Corps du rapport',
    icon: <BarChart fontSize="small" />,
    color: '#10b981',
    keys: ['methodology', 'analysis', 'memberContributions'],
  },
  {
    label: 'Finances',
    icon: <AttachMoney fontSize="small" />,
    color: '#f59e0b',
    keys: ['budget'],
  },
  {
    label: 'Conclusion',
    icon: <TrendingUp fontSize="small" />,
    color: '#8b5cf6',
    keys: ['conclusion', 'recommendations', 'lessonsLearned', 'perspectives'],
  },
];

const ADMIN_ROLES = ['owner', 'admin'];

const fmtCurrency = (amount, currency = 'XAF') => {
  const n = parseFloat(amount) || 0;
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n) + ' ' + currency;
};

// ─── Member view (non-admin) ──────────────────────────────────────────────────
function MemberReportsView({ projects }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const loadReports = async (project) => {
    setSelectedProject(project);
    setLoading(true);
    try {
      const res = await reportService.listReports(project.id);
      setReports(res.data.reports || []);
    } catch {
      setErrorMsg('Impossible de charger les rapports.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report, format) => {
    setDownloading(`${report.id}-${format}`);
    try {
      const res = format === 'pdf' ? await reportService.downloadPDF(report.id) : await reportService.downloadWord(report.id);
      const ext = format === 'pdf' ? 'pdf' : 'docx';
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${report.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch { setErrorMsg('Erreur lors du téléchargement.'); }
    finally { setDownloading(null); }
  };

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Article fontSize="large" color="primary" /> Rapports de projet
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'info.50', borderRadius: 2, border: '1px solid', borderColor: 'info.light' }}>
          <Lock fontSize="small" color="info" />
          <Typography variant="body2" color="info.dark">
            Vous pouvez consulter et télécharger les rapports publiés. Seuls les administrateurs peuvent en créer.
          </Typography>
        </Box>
      </Box>

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>Sélectionnez un projet</Typography>
        <Grid container spacing={2}>
          {projects.map(p => (
            <Grid item xs={12} sm={6} md={4} key={p.id}>
              <Card onClick={() => loadReports(p)} sx={{ cursor: 'pointer', border: '2px solid', borderColor: selectedProject?.id === p.id ? 'primary.main' : 'divider', bgcolor: selectedProject?.id === p.id ? 'primary.50' : 'background.paper', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 2 }, position: 'relative' }}>
                {selectedProject?.id === p.id && <CheckCircle sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.main', fontSize: 20 }} />}
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color || '#3498db', mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>{p.title}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">{p.total_tasks || 0} tâches · {p.team_members || 0} membres</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {selectedProject && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>Rapports disponibles — {selectedProject.title}</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : reports.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Article sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>Aucun rapport publié pour ce projet.</Typography>
              <Typography variant="caption">L'administrateur n'a pas encore partagé de rapport.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Titre</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Auteur</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Télécharger</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map(report => (
                    <TableRow key={report.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{report.title}</Typography></TableCell>
                      <TableCell><Chip label={report.report_type} size="small" variant="outlined" /></TableCell>
                      <TableCell>{report.author_name}</TableCell>
                      <TableCell><Typography variant="body2" color="text.secondary">{new Date(report.created_at).toLocaleDateString('fr-FR')}</Typography></TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Button size="small" variant="outlined" color="error" startIcon={downloading === `${report.id}-pdf` ? <CircularProgress size={14} /> : <PictureAsPdf />} onClick={() => handleDownload(report, 'pdf')} disabled={!!downloading}>PDF</Button>
                          <Button size="small" variant="outlined" startIcon={downloading === `${report.id}-word` ? <CircularProgress size={14} /> : <Description />} onClick={() => handleDownload(report, 'word')} disabled={!!downloading}>Word</Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}

// ─── Section selector component ──────────────────────────────────────────────
function SectionSelector({ selectedSections, onChange }) {
  const allSelected = ALL_SECTION_KEYS.every(k => selectedSections[k]);

  const toggleAll = () => {
    const next = {};
    ALL_SECTION_KEYS.forEach(k => { next[k] = !allSelected; });
    onChange(next);
  };

  const toggleSection = (key) => {
    onChange({ ...selectedSections, [key]: !selectedSections[key] });
  };

  const selectedCount = ALL_SECTION_KEYS.filter(k => selectedSections[k]).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>Sélectionnez les sections à inclure</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${selectedCount} / ${ALL_SECTION_KEYS.length} sections`} size="small" color="primary" variant="outlined" />
          <Button size="small" startIcon={allSelected ? <CheckBoxOutlineBlank /> : <CheckBox />} onClick={toggleAll} sx={{ textTransform: 'none' }}>
            {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2}>
        {SECTION_GROUPS.map(group => (
          <Grid item xs={12} sm={6} key={group.label}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: group.color + '40' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ color: group.color }}>{group.icon}</Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: group.color, letterSpacing: 0.5 }}>
                  {group.label.toUpperCase()}
                </Typography>
              </Box>
              {group.keys.map(key => (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!selectedSections[key]}
                        onChange={() => toggleSection(key)}
                        sx={{ '& .MuiSwitch-track': { bgcolor: selectedSections[key] ? group.color : undefined } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: selectedSections[key] ? 'text.primary' : 'text.disabled', fontWeight: selectedSections[key] ? 500 : 400 }}>{SECTION_LABELS[key]}</Typography>}
                    sx={{ m: 0, width: '100%' }}
                  />
                </Box>
              ))}
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

// ─── Budget card ──────────────────────────────────────────────────────────────
function BudgetCard({ budgetData }) {
  if (!budgetData || (budgetData.total === 0 && budgetData.spent === 0)) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <AttachMoney sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
        <Typography variant="body2" color="text.secondary">Aucun budget défini pour ce projet.</Typography>
        <Typography variant="caption" color="text.disabled">Ajoutez un budget dans l'onglet Finance du projet.</Typography>
      </Box>
    );
  }
  const spentPct = budgetData.spentPct || 0;
  const barColor = spentPct > 90 ? 'error' : spentPct > 75 ? 'warning' : 'success';

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Budget total', value: fmtCurrency(budgetData.total, budgetData.currency), color: 'primary.main', bg: 'primary.50' },
          { label: 'Dépenses', value: fmtCurrency(budgetData.spent, budgetData.currency), color: spentPct > 90 ? 'error.main' : 'warning.main', bg: spentPct > 90 ? 'error.50' : 'warning.50' },
          { label: 'Restant', value: fmtCurrency(budgetData.remaining, budgetData.currency), color: budgetData.remaining > 0 ? 'success.main' : 'error.main', bg: budgetData.remaining > 0 ? 'success.50' : 'error.50' },
        ].map(({ label, value, color, bg }) => (
          <Grid item xs={4} key={label}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: bg, border: '1px solid', borderColor: color + '40', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color, mt: 0.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}>{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>Taux de consommation</Typography>
          <Typography variant="caption" fontWeight={700} color={barColor + '.main'}>{spentPct}%</Typography>
        </Box>
        <LinearProgress variant="determinate" value={Math.min(spentPct, 100)} color={barColor} sx={{ height: 10, borderRadius: 1 }} />
      </Box>
      {budgetData.expensesByCategory?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary">DÉPENSES PAR CATÉGORIE</Typography>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Catégorie</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">Montant</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">% Budget</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {budgetData.expensesByCategory.map(cat => {
                  const pct = budgetData.total > 0 ? ((parseFloat(cat.total) / budgetData.total) * 100).toFixed(1) : '—';
                  return (
                    <TableRow key={cat.category} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}><Chip label={cat.category || 'Autre'} size="small" variant="outlined" /></TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600 }} align="right">{fmtCurrency(parseFloat(cat.total), budgetData.currency)}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }} align="right">{pct}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

// ─── Member stats card ────────────────────────────────────────────────────────
function MemberStatsCard({ memberStats, currency }) {
  if (!memberStats?.length) return null;
  const hasCost = memberStats.some(m => m.cost > 0);

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Membre</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Rôle</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Tâches</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Terminées</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Score</TableCell>
            {hasCost && <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="right">Coût</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {memberStats.map((m, i) => {
            const score = m.score;
            const scoreColor = score === null ? 'text.disabled' : score >= 75 ? 'success.main' : score >= 50 ? 'warning.main' : 'error.main';
            return (
              <TableRow key={i} hover>
                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500 }}>{m.full_name}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>
                  <Chip label={m.role} size="small" variant="outlined" />
                  {m.functional_role && <Typography variant="caption" color="text.secondary" display="block">{m.functional_role}</Typography>}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{m.total_tasks || 0}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{m.completed_tasks || 0}</TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={700} sx={{ color: scoreColor }}>
                    {score !== null ? `${score}%` : '—'}
                  </Typography>
                </TableCell>
                {hasCost && <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{m.cost > 0 ? fmtCurrency(m.cost, currency) : '—'}</TableCell>}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ReportPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);

  // Selected sections (toggle per section)
  const [selectedSections, setSelectedSections] = useState(
    Object.fromEntries(ALL_SECTION_KEYS.map(k => [k, true]))
  );

  // Cover info
  const [cover, setCover] = useState({
    title: 'Rapport de Projet',
    reportType: 'Rapport de suivi',
    organization: '',
    authors: '',
    date: new Date().toLocaleDateString('fr-FR'),
  });

  // Text sections
  const [sections, setSections] = useState(
    Object.fromEntries(ALL_SECTION_KEYS.map(k => [k, '']))
  );

  const [annexes, setAnnexes] = useState({ glossary: [], references: [], additionalNotes: '' });
  const [projectStats, setProjectStats] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [memberStats, setMemberStats] = useState([]);
  const [newGlossaryTerm, setNewGlossaryTerm] = useState({ term: '', definition: '' });
  const [newReference, setNewReference] = useState('');
  const [regeneratingSection, setRegeneratingSection] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [savedReportId, setSavedReportId] = useState(null);
  const [projectData, setProjectData] = useState(null);

  useEffect(() => {
    projectService.getAll().then(res => {
      setProjects(res.data.projects || res.data || []);
    }).catch(console.error).finally(() => setLoadingProjects(false));
  }, []);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setUserRole(project.user_role);
    setCover(prev => ({
      ...prev,
      title: `Rapport — ${project.title}`,
      authors: project.created_by_name || '',
    }));
    setSavedReportId(null);
  };

  const getSelectedSectionKeys = () => ALL_SECTION_KEYS.filter(k => selectedSections[k]);

  const handleGenerateAll = async () => {
    if (!selectedProject) return;
    setGenerating(true);
    setGenerationProgress(10);
    setErrorMsg('');
    try {
      setGenerationProgress(30);
      const selKeys = getSelectedSectionKeys();
      const res = await reportService.generate(selectedProject.id, { selectedSections: selKeys });
      const data = res.data.data;
      setGenerationProgress(80);
      setSections(prev => ({ ...prev, ...(data.sections || {}) }));
      setCover(prev => ({ ...prev, authors: data.cover?.authors || prev.authors }));
      setProjectStats(data.results?.statistics || null);
      setBudgetData(data.budgetData || null);
      setMemberStats(data.memberStats || []);
      setProjectData(data.projectData || null);
      setGenerationProgress(100);
      setSuccessMsg('Rapport généré ! Éditez les sections puis enregistrez.');
      setTimeout(() => { setSuccessMsg(''); setActiveStep(1); }, 1800);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Erreur lors de la génération du rapport.');
    } finally {
      setGenerating(false);
      setTimeout(() => setGenerationProgress(0), 1000);
    }
  };

  const handleRegenerateSection = async (sectionKey) => {
    if (!selectedProject) return;
    setRegeneratingSection(sectionKey);
    try {
      const res = await reportService.generate(selectedProject.id, { selectedSections: [sectionKey] });
      const data = res.data.data;
      if (data.sections?.[sectionKey]) {
        setSections(prev => ({ ...prev, [sectionKey]: data.sections[sectionKey] }));
      }
    } catch {
      setErrorMsg('Impossible de régénérer cette section.');
    } finally {
      setRegeneratingSection(null);
    }
  };

  const buildPayload = useCallback(() => ({
    cover: { ...cover, selectedSections: getSelectedSectionKeys() },
    sections,
    selectedSections: getSelectedSectionKeys(),
    results: { statistics: projectStats },
    budgetData,
    memberStats,
    annexes,
    projectData,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [cover, sections, selectedSections, annexes, projectStats, budgetData, memberStats, projectData]);

  const handleSave = async () => {
    if (!selectedProject) return;
    setSaving(true);
    try {
      const res = await reportService.save(selectedProject.id, buildPayload());
      setSavedReportId(res.data.reportId);
      setSuccessMsg('Rapport publié — les membres peuvent maintenant le consulter.');
      setTimeout(() => setSuccessMsg(''), 6000);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format) => {
    if (!selectedProject) return;
    setExporting(true);
    setErrorMsg('');
    try {
      const payload = buildPayload();
      const res = format === 'pdf' ? await reportService.exportPDF(selectedProject.id, payload) : await reportService.exportWord(selectedProject.id, payload);
      const ext = format === 'pdf' ? 'pdf' : 'docx';
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `rapport_${selectedProject.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      setSuccessMsg(`Rapport ${format.toUpperCase()} téléchargé avec succès.`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || `Erreur lors de l'export ${format.toUpperCase()}.`);
    } finally {
      setExporting(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0); setSelectedProject(null); setSavedReportId(null);
    setSections(Object.fromEntries(ALL_SECTION_KEYS.map(k => [k, ''])));
    setSelectedSections(Object.fromEntries(ALL_SECTION_KEYS.map(k => [k, true])));
    setProjectStats(null); setBudgetData(null); setMemberStats([]); setProjectData(null);
    setAnnexes({ glossary: [], references: [], additionalNotes: '' });
  };

  if (loadingProjects) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const isAdminOnAny = projects.some(p => ADMIN_ROLES.includes(p.user_role));
  if (!isAdminOnAny) return <MemberReportsView projects={projects} />;

  // ── Step renderers ──────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>Sélectionnez le projet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Seuls les projets dont vous êtes propriétaire ou administrateur sont disponibles.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {projects.filter(p => ADMIN_ROLES.includes(p.user_role)).map(p => (
          <Grid item xs={12} sm={6} md={4} key={p.id}>
            <Card
              onClick={() => handleProjectSelect(p)}
              sx={{ cursor: 'pointer', border: '2px solid', borderColor: selectedProject?.id === p.id ? 'primary.main' : 'divider', bgcolor: selectedProject?.id === p.id ? 'primary.50' : 'background.paper', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 3 }, position: 'relative' }}
            >
              {selectedProject?.id === p.id && <CheckCircle sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.main', fontSize: 20 }} />}
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color || '#3498db', mr: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1 }}>{p.title}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">{p.total_tasks || 0} tâches · {p.team_members || 0} membres</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip label={p.user_role === 'owner' ? 'Propriétaire' : 'Admin'} size="small" color="primary" />
                  {p.domain && <Chip label={p.domain} size="small" variant="outlined" />}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {selectedProject && (
        <>
          <Divider sx={{ my: 3 }} />

          {/* Page de garde */}
          <Typography variant="h6" fontWeight={600} gutterBottom>Page de garde</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Titre du rapport" value={cover.title} onChange={e => setCover(p => ({ ...p, title: e.target.value }))} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de rapport</InputLabel>
                <Select value={cover.reportType} label="Type de rapport" onChange={e => setCover(p => ({ ...p, reportType: e.target.value }))}>
                  {['Rapport de suivi', 'Rapport final', "Rapport d'avancement", 'Rapport exécutif', "Rapport d'audit", 'Rapport de clôture', 'Rapport intermédiaire'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Organisation / Client" value={cover.organization} onChange={e => setCover(p => ({ ...p, organization: e.target.value }))} size="small" InputProps={{ startAdornment: <Business sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Auteur(s)" value={cover.authors} onChange={e => setCover(p => ({ ...p, authors: e.target.value }))} size="small" InputProps={{ startAdornment: <Person sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Date du rapport" value={cover.date} onChange={e => setCover(p => ({ ...p, date: e.target.value }))} size="small" InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Section selector */}
          <SectionSelector selectedSections={selectedSections} onChange={setSelectedSections} />

          <Divider sx={{ my: 3 }} />

          {/* Generate button */}
          <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px dashed', borderColor: 'primary.main' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesome color="primary" /> Génération automatique par IA
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              L'IA analyse toutes les données du projet (tâches, membres, budget, jalons) et rédige les sections sélectionnées.
            </Typography>
            <Button
              variant="contained" size="large"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              onClick={handleGenerateAll} disabled={generating || getSelectedSectionKeys().length === 0}
            >
              {generating ? 'Génération en cours...' : `Générer ${getSelectedSectionKeys().length} section${getSelectedSectionKeys().length > 1 ? 's' : ''} avec l'IA`}
            </Button>
            {generating && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={generationProgress} sx={{ borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Analyse du projet et rédaction des sections en parallèle...
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const renderStep1 = () => {
    const activeSections = ALL_SECTION_KEYS.filter(k => selectedSections[k]);
    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>Contenu du rapport</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Éditez chaque section. Cliquez sur <AutoAwesome fontSize="small" sx={{ verticalAlign: 'middle' }} /> pour régénérer une section avec l'IA.
          Seules les sections sélectionnées à l'étape précédente sont affichées.
        </Typography>
        {activeSections.length === 0 && (
          <Alert severity="warning">Aucune section sélectionnée. Retournez à l'étape précédente pour en activer.</Alert>
        )}
        {activeSections.map(key => (
          <Accordion key={key} defaultExpanded={key === 'executiveSummary' || key === 'object'} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
              <Typography fontWeight={600} sx={{ flex: 1 }}>{SECTION_LABELS[key]}</Typography>
              {sections[key]?.trim() ? (
                <Chip label="Rédigé" size="small" color="success" sx={{ mr: 1 }} />
              ) : (
                <Chip label="Vide" size="small" variant="outlined" color="default" sx={{ mr: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                {SECTION_HINTS[key]}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth multiline minRows={4} maxRows={14}
                  value={sections[key]}
                  onChange={e => setSections(prev => ({ ...prev, [key]: e.target.value }))}
                  placeholder={`Rédigez ou générez la section "${SECTION_LABELS[key]}"...`}
                  variant="outlined" size="small"
                />
                <Tooltip title="Régénérer cette section avec l'IA">
                  <IconButton
                    size="small" onClick={() => handleRegenerateSection(key)}
                    disabled={regeneratingSection === key || !selectedProject}
                    sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
                  >
                    {regeneratingSection === key ? <CircularProgress size={16} /> : <AutoAwesome fontSize="small" color="primary" />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {(sections[key] || '').split(/\s+/).filter(Boolean).length} mots
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" fontWeight={600} gutterBottom>Annexes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Ajoutez un glossaire, des références bibliographiques ou des notes. L'équipe et la liste des tâches sont automatiquement incluses.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Glossaire</Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Terme" value={newGlossaryTerm.term} onChange={e => setNewGlossaryTerm(p => ({ ...p, term: e.target.value }))} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Définition" value={newGlossaryTerm.definition} onChange={e => setNewGlossaryTerm(p => ({ ...p, definition: e.target.value }))} /></Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="outlined" startIcon={<Add />} onClick={() => { if (newGlossaryTerm.term.trim()) { setAnnexes(p => ({ ...p, glossary: [...p.glossary, { ...newGlossaryTerm }] })); setNewGlossaryTerm({ term: '', definition: '' }); } }} sx={{ height: '100%' }}>Ajouter</Button>
          </Grid>
        </Grid>
        {annexes.glossary.length > 0 ? (
          <List dense>
            {annexes.glossary.map((item, i) => (
              <ListItem key={i} sx={{ bgcolor: 'background.default', mb: 0.5, borderRadius: 1 }}>
                <ListItemText primary={<Typography variant="body2" fontWeight={600}>{item.term}</Typography>} secondary={item.definition} />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => setAnnexes(p => ({ ...p, glossary: p.glossary.filter((_, idx) => idx !== i) }))}><Delete fontSize="small" /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 1 }}>Aucun terme ajouté</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Références bibliographiques</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField fullWidth size="small" label="Référence (auteur, titre, année, URL...)" value={newReference} onChange={e => setNewReference(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newReference.trim()) { setAnnexes(p => ({ ...p, references: [...p.references, newReference.trim()] })); setNewReference(''); } }} />
          <Button variant="outlined" startIcon={<Add />} onClick={() => { if (newReference.trim()) { setAnnexes(p => ({ ...p, references: [...p.references, newReference.trim()] })); setNewReference(''); } }}>Ajouter</Button>
        </Box>
        {annexes.references.length > 0 ? (
          <List dense>
            {annexes.references.map((ref, i) => (
              <ListItem key={i} sx={{ bgcolor: 'background.default', mb: 0.5, borderRadius: 1 }}>
                <ListItemText primary={<Typography variant="body2">[{i + 1}] {ref}</Typography>} />
                <ListItemSecondaryAction>
                  <IconButton size="small" onClick={() => setAnnexes(p => ({ ...p, references: p.references.filter((_, idx) => idx !== i) }))}><Delete fontSize="small" /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        ) : <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 1 }}>Aucune référence ajoutée</Typography>}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Notes complémentaires</Typography>
        <TextField fullWidth multiline minRows={3} size="small" label="Documents annexes, comptes-rendus, données brutes..." value={annexes.additionalNotes} onChange={e => setAnnexes(p => ({ ...p, additionalNotes: e.target.value }))} />
      </Paper>
    </Box>
  );

  const renderStep3 = () => {
    const sectionsReady = ALL_SECTION_KEYS.filter(k => selectedSections[k] && sections[k]?.trim()).length;
    const sectionsSelected = getSelectedSectionKeys().length;

    return (
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>Aperçu & Export</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Vérifiez les données, publiez le rapport pour votre équipe, ou exportez-le directement.
        </Typography>

        {/* Rapport summary */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Résumé du rapport</Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Projet', value: selectedProject?.title || '—' },
              { label: 'Type', value: cover.reportType },
              { label: 'Auteur(s)', value: cover.authors || '—' },
              { label: 'Sections sélectionnées', value: `${sectionsSelected}` },
              { label: 'Sections rédigées', value: `${sectionsReady} / ${sectionsSelected}` },
              { label: 'Tâches', value: String(projectStats?.total || 0) },
              { label: 'Avancement', value: `${projectStats?.progressPct || 0}%` },
              { label: 'Membres', value: String(projectStats?.membersCount || 0) },
              { label: 'Jalons', value: String(projectStats?.milestonesCount || 0) },
              { label: 'Mots (glossaire)', value: String(annexes.glossary.length) },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                <Typography variant="body2" fontWeight={600}>{value}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Budget */}
        {selectedSections['budget'] && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney color="warning" /> Budget & Finances
            </Typography>
            <BudgetCard budgetData={budgetData} />
          </Paper>
        )}

        {/* Member contributions */}
        {selectedSections['memberContributions'] && memberStats.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="primary" /> Implication des membres
            </Typography>
            <MemberStatsCard memberStats={memberStats} currency={budgetData?.currency || 'XAF'} />
          </Paper>
        )}

        {/* Publish */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Save color="success" /> Publier pour les membres
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En publiant, le rapport sera visible et téléchargeable par tous les membres du projet.
          </Typography>
          {savedReportId ? (
            <Alert severity="success">Rapport publié (ID #{savedReportId}) — les membres peuvent maintenant le consulter.</Alert>
          ) : (
            <Button variant="contained" color="success" size="large"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave} disabled={saving || !selectedProject}
            >
              {saving ? 'Publication...' : 'Publier le rapport'}
            </Button>
          )}
        </Paper>

        {/* Export */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ border: '2px solid', borderColor: 'error.light', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }, transition: 'all 0.2s' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <PictureAsPdf sx={{ fontSize: 48, color: 'error.main', mb: 1.5 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>Export PDF</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Rapport professionnel avec mise en page complète, graphiques et sommaire détaillé.
                </Typography>
                <Button variant="contained" color="error" fullWidth size="large"
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                  onClick={() => handleExport('pdf')} disabled={exporting || !selectedProject}
                >
                  {exporting ? 'Export en cours...' : 'Télécharger PDF'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card sx={{ border: '2px solid', borderColor: 'primary.light', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }, transition: 'all 0.2s' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Description sx={{ fontSize: 48, color: 'primary.main', mb: 1.5 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>Export Word</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Document .docx entièrement éditable avec tableaux et structure professionnelle.
                </Typography>
                <Button variant="contained" fullWidth size="large"
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <Description />}
                  onClick={() => handleExport('word')} disabled={exporting || !selectedProject}
                >
                  {exporting ? 'Export en cours...' : 'Télécharger Word'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const stepContent = [renderStep0, renderStep1, renderStep2, renderStep3];
  const canNext = [
    () => !!selectedProject && getSelectedSectionKeys().length > 0,
    () => ALL_SECTION_KEYS.some(k => selectedSections[k] && sections[k]?.trim()),
    () => true,
    () => false,
  ];

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Article fontSize="large" color="primary" /> Génération de rapports
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Créez des rapports professionnels complets grâce à l'IA — sélectionnez les sections, personnalisez le contenu, exportez en PDF ou Word.
        </Typography>
      </Box>

      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}
      {errorMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map((step, index) => (
          <Step key={step.label} completed={activeStep > index}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: { xs: 2, md: 4 }, mb: 3 }}>
        {stepContent[activeStep]()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setActiveStep(s => s - 1)} disabled={activeStep === 0}>
          Précédent
        </Button>
        <Typography variant="body2" color="text.secondary">Étape {activeStep + 1} / {STEPS.length}</Typography>
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" endIcon={<ArrowForward />} onClick={() => setActiveStep(s => s + 1)} disabled={!canNext[activeStep]()}>
            Suivant
          </Button>
        ) : (
          <Button variant="outlined" onClick={handleReset} startIcon={<Refresh />}>
            Nouveau rapport
          </Button>
        )}
      </Box>
    </Box>
  );
}
